import { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useLogin } from '../Contexts/AuthProvider/AuthProvider';
import { styled, useTheme } from '@mui/material/styles';
import {
  Box,
  Drawer,
  IconButton,
  useMediaQuery,
  AppBar,
  Toolbar,
  Typography,
  CssBaseline,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Assignment as RequestIcon,
  Edit as EditorIcon,
  ListAlt as WaitListIcon,
  ExitToApp as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
} from '@mui/icons-material';

const DRAWER_WIDTH = 200;
const DRAWER_WIDTH_COLLAPSED = 56;

const StyledDrawer = styled(Drawer, { 
  shouldForwardProp: (prop) => prop !== 'open' 
})<{ open: boolean }>(({ theme, open }) => ({
  width: open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    width: open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED,
    backgroundColor: '#0f172a',
    color: 'white',
    borderRight: 'none',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

const menuItems = [
  {
    title: 'Requests',
    description: 'Manage enrollment requests',
    icon: <RequestIcon sx={{ fontSize: 18 }} />,
    path: '/backoffice/request-list',
  },
  {
    title: 'Editor',
    description: 'Design and manage your landing pages',
    icon: <EditorIcon sx={{ fontSize: 18 }} />,
    path: '/backoffice/editor',
  },
  {
    title: 'Waitlist',
    description: 'Manage your waitlist entries',
    icon: <WaitListIcon sx={{ fontSize: 18 }} />,
    path: '/backoffice/waitlist',
  }
];

export default function ResponsiveMenu() {
  const { user, logout } = useLogin();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setOpen(!open);
    }
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isSelected = (item: typeof menuItems[0]) => {
    if (item.path === '/backoffice/request-list') {
      return location.pathname === '/backoffice' || 
             location.pathname === '/backoffice/' ||
             location.pathname.startsWith('/backoffice/request-list');
    }
    return location.pathname.startsWith(item.path);
  };

  const getCurrentPageInfo = () => {
    const currentItem = menuItems.find(item => isSelected(item));
    return {
      title: currentItem?.title || 'Dashboard',
      description: currentItem?.description || '',
    };
  };

  // Si no hay usuario, el ProtectedRoute ya redirige al login
  if (!user) {
    return null;
  }

  const drawerContent = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
    }}>
      {/* Header */}
      <Box sx={{ 
        px: 1.5,
        py: 1.5,
        display: 'flex', 
        alignItems: 'center',
        justifyContent: open || isMobile ? 'space-between' : 'center',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        {(open || isMobile) && (
          <Typography 
            sx={{ 
              fontSize: '0.8rem',
              fontWeight: 700,
              letterSpacing: '0.05em',
              color: '#E91E8C',
            }}
          >
            BACKOFFICE
          </Typography>
        )}
        {!isMobile && (
          <IconButton 
            onClick={handleDrawerToggle} 
            size="small"
            sx={{ 
              color: 'rgba(255,255,255,0.5)',
              p: 0.5,
              '&:hover': { 
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.05)',
              },
            }}
          >
            <ChevronLeftIcon sx={{ 
              fontSize: 18,
              transform: open ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'transform 0.2s',
            }} />
          </IconButton>
        )}
      </Box>
      
      {/* Menu Items */}
      <Box sx={{ flex: 1, py: 1 }}>
        {menuItems.map((item) => {
          const selected = isSelected(item);
          return (
            <Tooltip 
              key={item.path} 
              title={!open && !isMobile ? item.title : ''} 
              placement="right"
              arrow
            >
              <Box
                onClick={() => handleMenuItemClick(item.path)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  mx: 1,
                  my: 0.25,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: selected ? 'rgba(233, 30, 140, 0.12)' : 'transparent',
                  color: selected ? '#fff' : 'rgba(255,255,255,0.6)',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: selected ? 'rgba(233, 30, 140, 0.18)' : 'rgba(255,255,255,0.05)',
                    color: '#fff',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: 0.75,
                    bgcolor: selected ? '#E91E8C' : 'transparent',
                    color: selected ? '#fff' : 'inherit',
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </Box>
                {(open || isMobile) && (
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: selected ? 600 : 400,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.title}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Logout */}
      <Box sx={{ 
        px: 1,
        py: 1,
        borderTop: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Tooltip title={!open && !isMobile ? 'Logout' : ''} placement="right" arrow>
          <Box
            onClick={handleLogout}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 1.25,
              py: 0.75,
              borderRadius: 1,
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.5)',
              transition: 'all 0.15s ease',
              '&:hover': {
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                color: '#f87171',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
            </Box>
            {(open || isMobile) && (
              <Typography sx={{ fontSize: '0.8rem' }}>
                Logout
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <CssBaseline />
      
      {/* Mobile AppBar */}
      {isMobile && (
        <AppBar 
          position="fixed" 
          elevation={0}
          sx={{ 
            bgcolor: '#0f172a',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <Toolbar variant="dense" sx={{ minHeight: 48 }}>
            <IconButton
              color="inherit"
              onClick={handleDrawerToggle}
              edge="start"
              size="small"
              sx={{ mr: 1.5 }}
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </IconButton>
            
            <Typography sx={{ flexGrow: 1, fontWeight: 600, fontSize: '0.9rem' }}>
              {getCurrentPageInfo().title}
            </Typography>
            
            <IconButton color="inherit" onClick={handleLogout} size="small">
              <LogoutIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Toolbar>
        </AppBar>
      )}

      {/* Desktop Drawer */}
      {!isMobile && (
        <StyledDrawer variant="permanent" open={open}>
          {drawerContent}
        </StyledDrawer>
      )}

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            width: DRAWER_WIDTH,
            bgcolor: '#0f172a',
            color: 'white',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: { 
            xs: '100%', 
            md: `calc(100% - ${open ? DRAWER_WIDTH : DRAWER_WIDTH_COLLAPSED}px)` 
          },
          minHeight: '100vh',
          mt: { xs: '48px', md: 0 },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* Desktop Header */}
        {!isMobile && (
          <Box
            sx={{
              bgcolor: '#fff',
              borderBottom: '1px solid #e2e8f0',
              px: 2.5,
              py: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}
          >
            <Box>
              <Typography 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1e293b',
                  fontSize: '0.9rem',
                }}
              >
                {getCurrentPageInfo().title}
              </Typography>
              <Typography 
                sx={{ 
                  color: '#94a3b8',
                  fontSize: '0.7rem',
                }}
              >
                {getCurrentPageInfo().description}
              </Typography>
            </Box>
          </Box>
        )}
        
        {/* Page Content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
